import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import NotesIcon from '@mui/icons-material/Notes';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const NAME_MAX_LENGTH = 100;
const CONTENT_MAX_LENGTH = 5000;
const PREVIEW_LENGTH = 70;

const previewOf = (content = '') => {
  const flat = String(content).replace(/\s+/g, ' ').trim();
  return flat.length > PREVIEW_LENGTH ? `${flat.slice(0, PREVIEW_LENGTH)}…` : flat;
};
const normalizeName = (name = '') => String(name).trim().toLowerCase();
const emptyForm = { name: '', content: '' };
const noErrors = { name: '', content: '' };

// Toolbar shown above the invoice Notes field: "Insert template" (menu of the
// company's saved notes, plus a Manage entry) and "Save as template" (saves the
// current Notes text under a name). All CRUD stays inside this component so the
// create dialog only has to hand over the notes value and an insert callback.
const InvoiceNoteTemplates = ({
  templates = [],
  notes = '',
  onInsert,
  invoiceClient,
  companyId,
  onTemplatesChanged
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(noErrors);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const canMutate = Boolean(invoiceClient && companyId);
  const hasNotes = Boolean(String(notes || '').trim());
  const busy = saving || deleting;

  const findByName = (name) => templates.find((tpl) => normalizeName(tpl.name) === normalizeName(name));
  const sameNameTemplate = form.name.trim() ? findByName(form.name) : null;
  // Saving from the Notes field under an existing name replaces that template;
  // editing an existing template may not take another template's name.
  const replacesTemplate = !editingTemplate && sameNameTemplate ? sameNameTemplate : null;
  const nameTaken = Boolean(editingTemplate && sameNameTemplate && sameNameTemplate._id !== editingTemplate._id);

  const closeMenu = () => setMenuAnchor(null);

  const openForm = (template, content = '') => {
    setEditingTemplate(template || null);
    setForm(template
      ? { name: template.name || '', content: template.content || '' }
      : { name: '', content: content || '' });
    setFormError(noErrors);
    setFormOpen(true);
  };

  const resetForm = () => {
    setFormOpen(false);
    setEditingTemplate(null);
    setForm(emptyForm);
    setFormError(noErrors);
  };

  const handleInsert = (template) => {
    if (onInsert) onInsert(template.content || '');
    closeMenu();
  };

  const handleFieldChange = (field) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [field]: value }));
    setFormError((current) => ({ ...current, [field]: '' }));
  };

  const handleSave = async () => {
    const values = { name: form.name.trim(), content: form.content.trim() };
    const errors = {
      name: !values.name
        ? t('Template name is required', { defaultValue: 'Template name is required' })
        : (nameTaken
          ? t('A template with this name already exists', { defaultValue: 'A template with this name already exists' })
          : ''),
      content: values.content
        ? ''
        : t('Template content is required', { defaultValue: 'Template content is required' })
    };
    if (errors.name || errors.content) {
      setFormError(errors);
      return;
    }
    if (!canMutate || saving) return;

    const target = editingTemplate || replacesTemplate;
    setSaving(true);
    try {
      if (target) {
        await invoiceClient.updateInvoiceNoteTemplate(target._id, values);
      } else {
        await invoiceClient.createInvoiceNoteTemplate({ companyId, ...values });
      }
      if (onTemplatesChanged) await onTemplatesChanged();
      resetForm();
      setFeedback({
        severity: 'success',
        message: target
          ? t('Template updated.', { defaultValue: 'Template updated.' })
          : t('Template saved.', { defaultValue: 'Template saved.' })
      });
    } catch (error) {
      console.error('Error saving invoice note template', error);
      setFeedback({
        severity: 'error',
        message: error.response?.data?.error || t('Failed to save template.', { defaultValue: 'Failed to save template.' })
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canMutate || !deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await invoiceClient.deleteInvoiceNoteTemplate(deleteTarget._id);
      if (onTemplatesChanged) await onTemplatesChanged();
      setDeleteTarget(null);
      setFeedback({ severity: 'success', message: t('Template deleted.', { defaultValue: 'Template deleted.' }) });
    } catch (error) {
      console.error('Error deleting invoice note template', error);
      setFeedback({
        severity: 'error',
        message: error.response?.data?.error || t('Failed to delete template.', { defaultValue: 'Failed to delete template.' })
      });
    } finally {
      setDeleting(false);
    }
  };

  const insertLabel = t('Insert template', { defaultValue: 'Insert template' });
  const saveLabel = t('Save as template', { defaultValue: 'Save as template' });

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        <Button
          size="small"
          startIcon={<NotesIcon />}
          endIcon={<ArrowDropDownIcon />}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          aria-haspopup="menu"
          aria-controls={menuAnchor ? 'invoice-note-templates-menu' : undefined}
          aria-expanded={menuAnchor ? 'true' : undefined}
        >
          {insertLabel}
        </Button>
        <Tooltip title={hasNotes ? '' : t('Type a note first', { defaultValue: 'Type a note first' })}>
          <span>
            <Button
              size="small"
              startIcon={<BookmarkAddOutlinedIcon />}
              onClick={() => openForm(null, notes)}
              disabled={!hasNotes || !canMutate}
            >
              {saveLabel}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Menu
        id="invoice-note-templates-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { minWidth: 280, maxWidth: 420 } } }}
      >
        {templates.length === 0 ? (
          <MenuItem disabled>
            <ListItemText
              primary={t('No saved templates yet', { defaultValue: 'No saved templates yet' })}
              secondary={t('Write a note, then choose Save as template.', { defaultValue: 'Write a note, then choose Save as template.' })}
            />
          </MenuItem>
        ) : templates.map((tpl) => (
          <MenuItem key={tpl._id} onClick={() => handleInsert(tpl)}>
            <ListItemText primary={tpl.name} secondary={previewOf(tpl.content)} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { closeMenu(); setManageOpen(true); }}>
          <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('Manage templates…', { defaultValue: 'Manage templates…' })} />
        </MenuItem>
      </Menu>

      <Dialog open={manageOpen} onClose={busy ? undefined : () => setManageOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('Note templates', { defaultValue: 'Note templates' })}</DialogTitle>
        <DialogContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('Reusable notes you can insert into any invoice.', { defaultValue: 'Reusable notes you can insert into any invoice.' })}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openForm(null, '')}
              disabled={!canMutate || busy}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('New template', { defaultValue: 'New template' })}
            </Button>
          </Stack>
          {templates.length === 0 ? (
            <Typography color="text.secondary">
              {t('No saved templates yet', { defaultValue: 'No saved templates yet' })}
            </Typography>
          ) : templates.map((tpl) => (
            <Box
              key={tpl._id}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">{tpl.name}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {tpl.content}
                </Typography>
              </Box>
              <Tooltip title={t('Edit')}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={`${t('Edit')} ${tpl.name}`}
                    onClick={() => openForm(tpl)}
                    disabled={!canMutate || busy}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('Delete')}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={`${t('Delete')} ${tpl.name}`}
                    onClick={() => setDeleteTarget(tpl)}
                    disabled={!canMutate || busy}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)} disabled={busy}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={saving ? undefined : resetForm} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingTemplate
            ? t('Edit template', { defaultValue: 'Edit template' })
            : t('Save note as template', { defaultValue: 'Save note as template' })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t('Template name', { defaultValue: 'Template name' })}
              placeholder={t('e.g. Payment terms', { defaultValue: 'e.g. Payment terms' })}
              value={form.name}
              onChange={handleFieldChange('name')}
              error={Boolean(formError.name)}
              helperText={
                formError.name
                || (replacesTemplate
                  ? t('Replaces the existing "{{name}}" template', {
                    name: replacesTemplate.name,
                    defaultValue: 'Replaces the existing "{{name}}" template'
                  })
                  : ' ')
              }
              inputProps={{ maxLength: NAME_MAX_LENGTH }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('Note', { defaultValue: 'Note' })}
              multiline
              minRows={3}
              maxRows={8}
              value={form.content}
              onChange={handleFieldChange('content')}
              error={Boolean(formError.content)}
              helperText={formError.content}
              inputProps={{ maxLength: CONTENT_MAX_LENGTH }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm} disabled={saving}>{t('Cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!canMutate || saving}>
            {saving
              ? <CircularProgress size={20} />
              : (editingTemplate
                ? t('Save')
                : (replacesTemplate
                  ? t('Replace template', { defaultValue: 'Replace template' })
                  : t('Save template', { defaultValue: 'Save template' })))}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={deleting ? undefined : () => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('Delete template', { defaultValue: 'Delete template' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Delete the "{{name}}" template? This cannot be undone.', {
              name: deleteTarget?.name || '',
              defaultValue: 'Delete the "{{name}}" template? This cannot be undone.'
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>{t('Cancel')}</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={!canMutate || deleting}>
            {deleting ? <CircularProgress size={20} /> : t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback?.severity || 'success'} variant="filled" onClose={() => setFeedback(null)}>
          {feedback?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InvoiceNoteTemplates;
